import * as React from "react";
import { RadiosEditor } from "../../../../../../../react-core/Html/RadiosEditor";
import { isNullOrEmpty } from "../../../../../../../react-core/Utils/Arrays";
import { SubAccountType } from "../../../../../../../Wrap/Constants/Enums/Domain/SubAccountType";
import { MoneyOrPercentageType } from "../../../../../../../Wrap/Constants/Enums/MoneyOrPercentageType";
import { Alert } from "../../../../../../Shared/Components/Alert";
import { AlertType } from "../../../../../../Shared/Entities/Constants/AlertConstants";
import { PercentageOrMoney } from "../../../../../../Shared/PercentageOrMoney/PercentageOrMoney";
import { IProcessComponentProps, ProcessComponent } from "../../../../../../Shared/Process/Process/ProcessComponent";
import { SectionHeader } from "../../SharedComponents";
import { ChargesHelper } from "../../Helpers/ChargesHelper";
import { AdviserChargesContents, AdviserChargesValidation, ChargeFromMap, ChargeFromTypes } from "../Resource/Contents";
import { ClientAccountStore, ProductDetailsStore } from "../../../../../Stroes/Variable";
import { SourceType } from "../../../../../../../Wrap/Constants/Enums/Domain/SourceType";
import { SubAccountTypeHelper } from "../../Helpers/SubAccountTypeHelper";
import { WizardLinter, EWizardLinterStatus } from "../../../../../../Shared/Process/Process/WizardLinter";
import { EWizardLinterHelper } from "../../../NewBusinessWizard/Resource/EWizardLinterHelper";
import { JsonDateParser } from "../../../../../../../react-core/Utils/JsonDateParser";
import { Action } from "../../../../../Actions/Action";
import { MoneyHelper } from "../../../../../../Shared/Common/MoneyHelper";
import { MoneyDisplayHelper } from "../../../../../../../react-core/Helpers/MoneyDisplayHelper";

interface IIACProps extends IProcessComponentProps {
    type: SubAccountType;
    existingAccount?: boolean;
    valueLimit?: number
}

interface IIACState {
    wrapcashCurrentTotal?: number;
}

type ChargeAllocation = {
    key: number;
    text: string;
}

export class IAC extends ProcessComponent<IIACProps, IIACState> {
    constructor(props: any) {
        super(props);
        this.state = {
            wrapcashCurrentTotal: null,
        };
        this.subscribeToStore(ProductDetailsStore, () => this.onProductDetailsChange());
    }

    componentDidMount() {
        super.componentDidMount();
        const { existingAccount, data, onChange } = this.props;
        if (existingAccount && !this.state.wrapcashCurrentTotal) {
            this.onProductDetailsChange();
        }
        if (!this.state.wrapcashCurrentTotal) {
            const clientAccount = ClientAccountStore.getClientAccountInfo();
            if (clientAccount?.SubAccounts?.some((x) => x.SubAccountType == SubAccountType.WrapCash)) {
                const _date = JsonDateParser.getFormatDateString(new Date(), "yyyy/MM/dd") as any;
                Action.getProductsValuationDetailSummary(
                    clientAccount?.SubAccounts?.find((a) => a.SubAccountType == SubAccountType.WrapCash).HierarchyId,
                    null,
                    _date
                );
            }
        }

        const sourceInstructionId = this.TryAutoDetermineSourceInstructionId();
        if (sourceInstructionId != null) {
            this.onDataChange(onChange, { SourceInstructionId: sourceInstructionId }, data, "InitialAdviserCharge");
        }
    }

    componentDidUpdate() {
        const { data, onChange, type } = this.props;
        const sourceType = (SubAccountTypeHelper.isOnPlatformPension(type) || type === SubAccountType.JuniorSipp) 
            ? SourceType.ProductCash 
            : SourceType.WrapCash;

        let changes = {};
        if (isNullOrEmpty(data?.InitialAdviserCharge?.SourceType) || data?.InitialAdviserCharge?.SourceType == 0) {
            changes["SourceType"] = sourceType;
        }
        const sourceInstructionId = this.TryAutoDetermineSourceInstructionId();
        if (sourceInstructionId != null) {
            changes["SourceInstructionId"] = sourceInstructionId;
        }
        if (Object.keys(changes).length > 0) {
            this.onDataChange(onChange, changes, data, "InitialAdviserCharge");
        }
    }

    onProductDetailsChange() {
        const result = ProductDetailsStore.getSubAccount(SubAccountType.WrapCash);
        if (result) {
            this.setState({
                wrapcashCurrentTotal: result.TotalValue?.Amount,
            });
        }
    }

    isPercentage(): boolean {
        const { data } = this.props;
        return (
            !data || (data.InitialAdviserCharge && data.InitialAdviserCharge.Type == MoneyOrPercentageType.Percentage)
        );
    }

    renderAlert(totalForEstimation: number) {
        const percentage = this.getPercentageBasedOnAmountOrPercentage(totalForEstimation);
        let alerts = [];
        if (this.iacGiven() && (SubAccountTypeHelper.isOnPlatformPension(this.props.type) || 
        this.props.type === SubAccountType.JuniorSipp)) {
            alerts.push(<Alert alertType={AlertType.Warning} message={AdviserChargesValidation.IACWarningMessage} />);
        }
        if (percentage >= 10) {
            alerts.push(
                <Alert
                    alertType={AlertType.Warning}
                    message={`You have requested an adviser payment of ${percentage.toFixed(
                        2
                    )}%. Please continue if this is correct.`}
                />
            );
        }
        if (
            (this.props.data?.InitialAdviserCharge?.SourceType == undefined ||
                this.props.data?.InitialAdviserCharge?.SourceType == SourceType.NotSpecified ||
                this.props.data?.InitialAdviserCharge?.SourceType == SourceType.WrapCash) &&
            this.iacGiven()
        ) {
            alerts.push(
                <Alert alertType={AlertType.Warning} message={AdviserChargesValidation.WrapCashIACWarningMessage} />
            );
        }
        if (!this.validateSufficientWrapCashForSPT(totalForEstimation) && this.props.data.InitialAdviserCharge.SourceType == SourceType.WrapCash) {
            alerts.push(
                <Alert alertType={AlertType.Warning} message={AdviserChargesValidation.InsufficientCashForIAC} />
            );
        }
        if (alerts.length > 0) {
            return <>{alerts}</>;
        }
    }

    validateSufficientWrapCashForSPT(totalForEstimation: number) {
        if (!this.state.wrapcashCurrentTotal || !this.iacGiven()) {
            return true;
        }
        const { data } = this.props;
        let iac = 0;
        if (
            data?.InitialAdviserCharge?.Type === MoneyOrPercentageType.Money &&
            data?.InitialAdviserCharge?.Money.Amount > 0
        ) {
            iac = data.InitialAdviserCharge.Money.Amount;
        } else if (
            data?.InitialAdviserCharge?.Type === MoneyOrPercentageType.Percentage &&
            data?.InitialAdviserCharge?.Percentage > 0
        ) {
            iac = (totalForEstimation * data.InitialAdviserCharge.Percentage) / 100;
        }
        return iac <= this.state.wrapcashCurrentTotal;
    }

    getPercentageBasedOnAmountOrPercentage(totalForEstimation: number) {
        const iac = this.props.data?.InitialAdviserCharge;
        let percentageValue = iac?.Percentage;
        if (iac && iac.Money && iac.Money.Amount && iac.Money.Amount > 0) {
            percentageValue = (iac.Money.Amount / totalForEstimation) * 100;
        }
        return percentageValue;
    }

    getTotalForEstimation(): number {
        return ChargesHelper.getTotalForIACEstimation(this.props.data, this.props.type);
    }

    iacGiven(): boolean {
        const amount = this.props.data?.InitialAdviserCharge?.Money;
        const percentage = this.props.data?.InitialAdviserCharge?.Percentage;
        return percentage || (amount && amount.Amount && amount.Amount > 0);
    }

    hasSinglePayment(data): boolean {
        return (data.ClientSinglePayment?.Money && data.ClientSinglePayment.Money.Amount > 0)
        || (data.ThirdPartySinglePayment?.Money && data.ThirdPartySinglePayment.Money.Amount > 0)
        || (data.EmployerSinglePayment?.Money && data.EmployerSinglePayment.Money.Amount > 0)
        || (data.EmployerSalaryDeductionSinglePayment?.Money && data.EmployerSalaryDeductionSinglePayment.Money.Amount > 0)
    }

    hasTransfer(data): boolean {
        return data.PensionTransfers.length > 0 && data.PensionTransfers.some(x => x.CurrentValue.Amount > 0);
    }

    hasMultiplePaymentIn(data): boolean {
        const singlePaymentsCount = [
            data.ClientSinglePayment?.Money?.Amount ?? 0,
            data.ThirdPartySinglePayment?.Money?.Amount ?? 0,
            data.EmployerSinglePayment?.Money?.Amount ?? 0,
            data.EmployerSalaryDeductionSinglePayment?.Money?.Amount ?? 0
        ].filter(amount => amount > 0).length;
    
        const transferCount = data.PensionTransfers.filter(t => t.CurrentValue?.Amount > 0).length;
    
        return (singlePaymentsCount + transferCount) > 1;
    }

    getChargeAllocations(data): Array<ChargeAllocation> {
        var amount = 0;
        var chargeAllocations: Array<ChargeAllocation> = new Array<ChargeAllocation>();

        var idx = 1;
        if (data.ClientSinglePayment?.Money && data.ClientSinglePayment.Money.Amount > 0) {
            amount = MoneyHelper.roundNumberToDecimalPlaces(data.ClientSinglePayment.Money.Amount, 2);
            var chargeAllocation: ChargeAllocation = {
                key: data.ClientSinglePayment.InstructionId,
                text: "Single Payment " + idx.toString() + " of £" + MoneyDisplayHelper.FromatMoneyDisplay(amount)
            }
            chargeAllocations.push(chargeAllocation);
            idx++;
        }

        if (data.ThirdPartySinglePayment?.Money && data.ThirdPartySinglePayment.Money.Amount > 0) {
            amount = MoneyHelper.roundNumberToDecimalPlaces(data.ThirdPartySinglePayment.Money.Amount, 2);
            var chargeAllocation: ChargeAllocation = {
                key: data.ThirdPartySinglePayment.InstructionId,
                text: "Single Payment " + idx.toString() + " of £" + MoneyDisplayHelper.FromatMoneyDisplay(amount)
            }
            chargeAllocations.push(chargeAllocation);
            idx++;
        }

        if (data.EmployerSinglePayment?.Money && data.EmployerSinglePayment.Money.Amount > 0) {
            amount = MoneyHelper.roundNumberToDecimalPlaces(data.EmployerSinglePayment.Money.Amount, 2);
            var chargeAllocation: ChargeAllocation = {
                key: data.EmployerSinglePayment.InstructionId,
                text: "Single Payment " + idx.toString() + " of £" + MoneyDisplayHelper.FromatMoneyDisplay(amount)
            }
            chargeAllocations.push(chargeAllocation);
            idx++;
        }

        if (data.EmployerSalaryDeductionSinglePayment?.Money && data.EmployerSalaryDeductionSinglePayment.Money.Amount > 0) {
            amount = MoneyHelper.roundNumberToDecimalPlaces(data.EmployerSalaryDeductionSinglePayment.Money.Amount, 2);
            var chargeAllocation: ChargeAllocation = {
                key: data.EmployerSalaryDeductionSinglePayment.InstructionId,
                text: "Single Payment " + idx.toString() + " of £" + MoneyDisplayHelper.FromatMoneyDisplay(amount)
            }
            chargeAllocations.push(chargeAllocation);
            idx++;
        }

        if (data.PensionTransfers) {
            let idx = 1;
            data.PensionTransfers.forEach(transfer => {
                if (transfer.CurrentValue.Amount > 0) {
                    amount = MoneyHelper.roundNumberToDecimalPlaces(transfer.CurrentValue.Amount, 2);
                    var chargeAllocation: ChargeAllocation = {
                        key: transfer.TransferId,
                        text: "Transfer Payment " + idx.toString() + " of £" + MoneyDisplayHelper.FromatMoneyDisplay(amount)
                    }
                    chargeAllocations.push(chargeAllocation);
                    idx++;
                }
            });
        }

        return chargeAllocations;
    }

    TryAutoDetermineSourceInstructionId(): number {
        const { data, type } = this.props;

        const isPensionProductCashAmountIac = (SubAccountTypeHelper.isOnPlatformPension(type) || type === SubAccountType.JuniorSipp) && !this.isPercentage() && data?.InitialAdviserCharge?.SourceType == SourceType.ProductCash;
        if (isPensionProductCashAmountIac && this.hasMultiplePaymentIn(data)) {
            return null;
        }

        let result: number = null;
        if (data?.InitialAdviserCharge?.SourceInstructionId > 0 && !isPensionProductCashAmountIac) {
            result = 0;
        }
        else if (!isPensionProductCashAmountIac) {
            return null;
        }
        else if (data.ClientSinglePayment?.Money && data.ClientSinglePayment.Money.Amount > 0) {
            result = data.ClientSinglePayment.InstructionId;
        }
        else if (data.ThirdPartySinglePayment?.Money && data.ClientSinglePayment.Money.Amount > 0) {
            result = data.ThirdPartySinglePayment.InstructionId
        }
        else if (data.EmployerSinglePayment?.Money && data.EmployerSinglePayment.Money.Amount > 0) {
            result = data.EmployerSinglePayment.InstructionId;
        }
        else if (data.EmployerSalaryDeductionSinglePayment?.Money && data.EmployerSalaryDeductionSinglePayment.Money.Amount > 0) {
            result = data.EmployerSalaryDeductionSinglePayment.InstructionId;
        }
        else if (data.PensionTransfers) {
            result = data.PensionTransfers.find(t =>
                t.CurrentValue?.Amount +
                t.Instruments?.reduce((acc, curr) => acc + curr.QuotationValue.Amount, 0)
                > 0)?.TransferId;
        }

        if (result != null && result != data?.InitialAdviserCharge?.SourceInstructionId) {
            return result;
        } else {
            return null;
        }
    }

    public render(): JSX.Element {
        const { data, onChange, type } = this.props;
        const totalForEstimation = this.getTotalForEstimation();
        if (totalForEstimation <= 0) {
            return null;
        }
        
        const chargeAllocations = this.getChargeAllocations(data);
        
        return (
            <WizardLinter status={EWizardLinterStatus.PleaseCheck} enabled={EWizardLinterHelper.shouldDisplay(type)}>
                {SectionHeader(
                    AdviserChargesContents.IAC,
                    "For monetary (£) Initial Adviser Charges – we try to take these straight away if there is cleared cash. For percentage (%) Initial Adviser Charges – we will pay this once the expected cash has been applied."
                )}
                {this.renderAlert(totalForEstimation)}
                <PercentageOrMoney
                    label={"Rate"}
                    data={data?.InitialAdviserCharge}
                    //tooltip={AdviserChargesContents.IACTooltip}
                    onChange={(v) => {
                        if (!data.InitialAdviserCharge?.SourceType || data.InitialAdviserCharge?.SourceType == SourceType.NotSpecified) {
                            data.InitialAdviserCharge.SourceType = SourceType.ProductCash;
                        }
                        this.onDataChange(onChange, v, data, "InitialAdviserCharge");
                    }}
                    totalForEstimation={totalForEstimation}
                    validationRules={[
                        {
                            errorMessage: AdviserChargesValidation.InsufficientCashForIAC,
                            validator: (v) => {
                                return true;
                            },
                        },
                    ]}
                    onRef={(ref) => this.appendChild(ref)}
                    cssWrapper="mb-4"
                    cssPercentageOrMoneyClasses="control-size-115"
                    valueLimit = {this.props.valueLimit}
                />

                {(SubAccountTypeHelper.isOnPlatformPension(type) || 
                type === SubAccountType.JuniorSipp) && (
                <>
                    <RadiosEditor
                        cssWrapper="rg-150 mb-24 mt-30"
                        label={"Take the charge from:"}
                        options={ChargeFromTypes}
                        selected={data?.InitialAdviserCharge?.SourceType??SourceType.ProductCash}
                        getText={(o) => ChargeFromMap[o]}
                        required={this.iacGiven() ? "required" : null}
                        onChange={(v) => this.onDataChange(onChange, { SourceType: v }, data, "InitialAdviserCharge")}
                    />

                    {!this.isPercentage() && data?.InitialAdviserCharge?.SourceType == SourceType.ProductCash && this.hasMultiplePaymentIn(data) && (
                        <RadiosEditor
                            cssWrapper="rg-inline mb-24 mt-30"
                            label={"Payment of initial adviser charge to be taken from:"}
                            options={chargeAllocations.map(x=>x.key)}
                            selected={data?.InitialAdviserCharge?.SourceInstructionId ?? 0}
                            getText={(o) => chargeAllocations.find(x=>x.key==o).text}
                            required={this.iacGiven() ? "required" : null}
                            onChange={(v) => this.onDataChange(onChange, { SourceInstructionId: v }, data, "InitialAdviserCharge")}
                        />
                    )}
                </>
                )}
                <hr className="mb-30" />
            </WizardLinter>
        );
    }
}
